import {
  convertServerTimeToLocal,
  getUnlocksInValues,
} from "@whoosh/utilities/src/dates";

import { getLiftAtForServerTimeOffset } from "../../../../Common/utils/serverTimeOffset";
import { TimeSlotListInternalQuery$data } from "../components/__generated__/TimeSlotListInternalQuery.graphql";

const hasExpiredLiftAt = (
  liftAt?: string | null,
  serverTimeOffsetMs?: number | null
) => {
  if (!liftAt) {
    return false;
  }

  const adjustedLiftAt = getLiftAtForServerTimeOffset(
    liftAt,
    serverTimeOffsetMs
  );
  const { daysLeft, hoursLeft, minutesLeft, secondsLeft } =
    getUnlocksInValues(adjustedLiftAt);

  return (
    daysLeft === 0 && hoursLeft === 0 && minutesLeft === 0 && secondsLeft === 0
  );
};

type GetRestrictionLiftAtsOptions = {
  includeConfiguredRequestRestrictions?: boolean;
};

type ConfiguredRequestRestrictionEdge =
  | {
      readonly requestCloses?: string | null;
      readonly requestOpens?: string | null;
    }
  | null
  | undefined;

const getConfiguredRequestRestrictionRefreshTimes = (
  restrictionEdges?:
    | ReadonlyArray<ConfiguredRequestRestrictionEdge>
    | null
    | undefined
) =>
  (restrictionEdges ?? [])
    .flatMap((restrictionEdge) => [
      restrictionEdge?.requestOpens,
      restrictionEdge?.requestCloses,
    ])
    .filter((refreshTime): refreshTime is string => !!refreshTime);

const getRestrictionLiftAts = (
  data: TimeSlotListInternalQuery$data,
  {
    includeConfiguredRequestRestrictions = false,
  }: GetRestrictionLiftAtsOptions = {}
) => {
  const agenda = data.node?.agendas?.edges?.[0]?.node;

  if (!agenda) {
    return [];
  }

  const agendaRestrictionEdges = [
    ...(agenda.blockingRestrictions?.edges ?? []),
    ...(agenda.infoRestrictions?.edges ?? []),
    ...(agenda.waitlistRestrictions?.edges ?? []),
    ...(agenda.requestRestrictions?.edges ?? []),
    ...(includeConfiguredRequestRestrictions
      ? agenda.configuredRequestRestrictions?.edges ?? []
      : []),
  ];
  const agendaConfiguredRequestRefreshTimes =
    includeConfiguredRequestRestrictions
      ? getConfiguredRequestRestrictionRefreshTimes(
          agenda.configuredRequestRestrictions?.edges
        )
      : [];

  const timeSlotRestrictionEdges = (agenda.timeSlots?.edges ?? []).flatMap(
    (timeSlotEdge) => {
      const timeSlot = timeSlotEdge?.node;

      return [
        ...(timeSlot?.requestRestrictions?.edges ?? []),
        ...(includeConfiguredRequestRestrictions
          ? timeSlot?.configuredRequestRestrictions?.edges ?? []
          : []),
        ...(timeSlot?.hiddenBlockingRestrictions?.edges ?? []),
        ...(timeSlot?.waitlistRestrictions?.edges ?? []),
      ];
    }
  );
  const timeSlotConfiguredRequestRefreshTimes = (
    agenda.timeSlots?.edges ?? []
  ).flatMap((timeSlotEdge) => {
    const timeSlot = timeSlotEdge?.node;

    return includeConfiguredRequestRestrictions
      ? getConfiguredRequestRestrictionRefreshTimes(
          timeSlot?.configuredRequestRestrictions?.edges
        )
      : [];
  });

  return [
    ...agendaRestrictionEdges.map((restrictionEdge) => restrictionEdge?.liftAt),
    ...agendaConfiguredRequestRefreshTimes,
    ...timeSlotRestrictionEdges.map(
      (restrictionEdge) => restrictionEdge?.liftAt
    ),
    ...timeSlotConfiguredRequestRefreshTimes,
  ].filter((liftAt): liftAt is string => !!liftAt);
};

const getAdjustedLiftAtMs = (
  liftAt: string,
  serverTimeOffsetMs?: number | null
) => {
  const adjustedLiftAt = getLiftAtForServerTimeOffset(
    liftAt,
    serverTimeOffsetMs
  );

  if (!adjustedLiftAt) {
    return null;
  }

  const liftAtMs = convertServerTimeToLocal(adjustedLiftAt).getTime();

  return Number.isNaN(liftAtMs) ? null : liftAtMs;
};

export const getNextClientRestrictionLiftAtMs = (
  data: TimeSlotListInternalQuery$data,
  serverTimeOffsetMs?: number | null,
  now = new Date()
) => {
  const nowMs = now.getTime();

  return (
    getRestrictionLiftAts(data, {
      includeConfiguredRequestRestrictions: true,
    })
      .map((liftAt) => getAdjustedLiftAtMs(liftAt, serverTimeOffsetMs))
      .filter(
        (liftAtMs): liftAtMs is number => liftAtMs !== null && liftAtMs > nowMs
      )
      .sort((a, b) => a - b)[0] ?? null
  );
};

export const hasClientRestrictionRefreshTimeElapsed = (
  nextLiftAtMs: number | null,
  now = new Date()
) => nextLiftAtMs !== null && nextLiftAtMs <= now.getTime();

export const hasClientExpiredRestrictions = (
  data: TimeSlotListInternalQuery$data,
  serverTimeOffsetMs?: number | null
) => {
  return getRestrictionLiftAts(data).some((liftAt) =>
    hasExpiredLiftAt(liftAt, serverTimeOffsetMs)
  );
};
